/**
 * The selected cells a spreadsheet context chip will send, shown inside the
 * chip on one line: `bower-eval-cases · [C32:C43 ×]`.
 *
 * The range is its own small pill (a lighter surface with a hairline, so it
 * reads as separate from the chip in both color modes) with its own remove
 * control, labeled with the range ("Stop using cells C32 to C43"), so the
 * researcher can stop sending the selected cells (and ask about the whole
 * sheet) without taking the spreadsheet out of context. The sheet name is NOT
 * shown here (David, 2026-09-28: one line, title plus range); it lives in the
 * chip's tooltip and accessible name instead.
 */
import { HStack, IconButton, Text } from '@chakra-ui/react';

import { MaterialIcon } from '../../ui/MaterialIcon';

interface ContextSelectionTagProps {
  readonly selection?: string;
  /** Omit to show the range without a way to remove it. */
  readonly onDismissSelection?: () => void;
}

/**
 * How a screen reader should say a range: "cells C32 to C43", "cell C5",
 * "column B", "columns B to D", "row 3", "rows 3 to 5". A whole column or row
 * is a valid selection (`validSelection`) and "cells B to D" would misname it.
 */
export function spokenRange(selection: string): string {
  const [from, to] = selection.split(':');
  let noun = 'cell';
  if (/^[A-Z]+$/i.test(from)) noun = 'column';
  else if (/^\d+$/.test(from)) noun = 'row';
  if (!to) return `${noun} ${from}`;
  if (noun !== 'cell' && from === to) return `${noun} ${from}`;
  return `${noun}s ${from} to ${to}`;
}

/** The chip's accessible name: "Context: Plate, sheet Raw, cells A1 to B2". */
export function contextChipName(label: string, sheet?: string, selection?: string): string {
  return [
    `Context: ${label}`,
    sheet ? `sheet ${sheet}` : null,
    selection ? spokenRange(selection) : null,
  ]
    .filter(Boolean)
    .join(', ');
}

export function ContextSelectionTag({ selection, onDismissSelection }: ContextSelectionTagProps) {
  if (!selection) return null;

  return (
    <>
      <Text as="span" fontSize="xs" color="text.secondary" aria-hidden>
        ·
      </Text>
      {/* Never truncated: the range is the point of the chip. Only the title
          gives way when space is short (ContextChip's `labelMaxW`).

          Its own surface and hairline, NOT `bg.subtle`: that resolves to the
          chip's own `bg.muted` value in both modes, which left the pill
          invisible at rest and the chip reading as two identical close
          buttons. `bg.surface` differs from `bg.muted` in both modes
          (#F8F7F4 vs #E5E3DE light, #1C211C vs #252B25 dark); `bg.elevated`
          does not in dark (#242A24), so the fill alone would vanish there. */}
      <HStack
        spacing={0}
        pl={1.5}
        borderRadius="sm"
        bg="bg.surface"
        border="1px solid"
        borderColor="border.default"
        flexShrink={0}
      >
        <Text fontSize="xs" fontWeight="medium" color="text.primary" whiteSpace="nowrap">
          {selection}
        </Text>
        {onDismissSelection && (
          <IconButton
            aria-label={`Stop using ${spokenRange(selection)}`}
            icon={<MaterialIcon name="close" boxSize={3} />}
            size="xs"
            variant="ghost"
            // ContextChip moves focus to the chip itself after this.
            onClick={onDismissSelection}
            // The theme's xs size (24x24), the WCAG 2.5.8 minimum target.
            borderRadius="sm"
            _hover={{ bg: 'bg.hover' }}
          />
        )}
      </HStack>
    </>
  );
}
